import * as React from "react";

import { cn } from "@/lib/utils";
import { DictateButton } from "@/components/DictateButton";

const DICTATABLE = new Set(["text", "search", undefined, ""]);

export type InputProps = React.ComponentProps<"input"> & {
  /** Set false to hide the voice-dictation microphone on this field. */
  dictation?: boolean;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, dictation, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [ref],
    );

    const canDictate =
      (dictation ?? true) && DICTATABLE.has(type as string | undefined) && !props.disabled && !props.readOnly;

    const field = (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          canDictate && "pr-11",
          className,
        )}
        ref={setRefs}
        {...props}
      />
    );

    if (!canDictate) return field;

    return (
      <div className="relative w-full">
        {field}
        <DictateButton
          targetRef={innerRef}
          className="absolute top-1/2 right-1.5 -translate-y-1/2"
        />
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
